import { useEffect, useState, useContext } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faMapMarkerAlt,
  faReceipt,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import Context from "../Context";
import "./receipt.css";

const API_URL = process.env.REACT_APP_API_URL || "https://hutaoadmin.onrender.com/api";

function compareOrderItemAttributesForReceipt(a, b) {
  const sa = a.option_scope_key != null ? String(a.option_scope_key) : "";
  const sb = b.option_scope_key != null ? String(b.option_scope_key) : "";
  if (sa !== sb) return sa.localeCompare(sb, undefined, { numeric: true });
  const groupA = a?.Item_Attribute_Option?.Item_Attribute?.name || "";
  const groupB = b?.Item_Attribute_Option?.Item_Attribute?.name || "";
  if (groupA !== groupB) return groupA.localeCompare(groupB);
  const optA = a?.Item_Attribute_Option?.name || "";
  const optB = b?.Item_Attribute_Option?.name || "";
  return optA.localeCompare(optB);
}

function modifierScopeDepth(scopeKey) {
  if (scopeKey == null || scopeKey === "") return 0;
  return Math.max(0, String(scopeKey).split(">").length - 1);
}

function Receipt() {
  const { id, restaurant_id } = useParams();
  const { restaurantId: contextRestaurantId } = useContext(Context);
  const homepage = useSelector((state) => state.restaurants?.homepage);
  const restaurant = useSelector((state) => state.restaurants?.restaurant);
  const [order, setOrder] = useState(null);
  const [showFees, setShowFees] = useState(false);

  const rid = restaurant_id || contextRestaurantId;
  const logoUrl = homepage?.logoImage || restaurant?.logo || restaurant?.logo_url || null;

  useEffect(() => {
    if (!id || !rid) return;
    const fetchOrder = async () => {
      try {
        const res = await fetch(`${API_URL}/orders/order/${id}/${rid}`);
        if (res.ok) {
          const data = await res.json();
          setOrder(data);
        }
      } catch (error) {
        console.error("Error fetching order:", error);
      }
    };
    fetchOrder();
  }, [id, rid]);

  const handleNavigate = () => {
    const street = order?.Restaurant?.street;
    const city = order?.Restaurant?.city;
    if (!street || !city) return;
    const encodedAddress = encodeURIComponent(`${street}, ${city}`);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const mapsUrl = isIOS
      ? `http://maps.apple.com/?daddr=${encodedAddress}`
      : `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
    window.open(mapsUrl, "_blank");
  };

  if (!order) {
    return (
      <div className="receipt-loading" style={{ marginTop: "120px" }}>
        <div className="spinner-border text-danger" role="status" />
      </div>
    );
  }

  const isDelivery = order?.address?.length > 9;
  const googleReviewUrl = order?.Restaurant?.google_review_url?.trim();
  const totalFeesAndTaxes =
    Number(order?.tax || 0) +
    Number(order?.convenience_fee || 0) +
    Number(order?.service_fee || 0) +
    Number(order?.processing_fee || 0) +
    Number(order?.delivery_fee || 0);

  return (
    <div className="receipt-page" style={{ marginTop: "100px" }}>
      <div className="receipt-paper">
        <div className="receipt-paper-inner">
          <div className="receipt-header-block">
            {logoUrl ? (
              <div className="receipt-logo-wrap">
                <img src={logoUrl} alt={order?.Restaurant?.name || "Restaurant"} />
              </div>
            ) : (
              <div className="receipt-logo-wrap">
                <FontAwesomeIcon icon={faReceipt} className="text-secondary fs-4" />
              </div>
            )}
            <h1 className="receipt-title">{order?.Restaurant?.name || "Order Receipt"}</h1>
            <div className="receipt-date">
              <div>
                {new Date(order.createdAt).toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
              <div>
                {new Date(order.createdAt).toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            </div>
          </div>

          <div className="receipt-order-id">
            <p className="receipt-order-id-label">Order Number</p>
            <p className="receipt-order-id-value">#{order.order_id}</p>
            {order.pickup_number && (
              <p className="receipt-pickup-num">Pickup #{order.pickup_number}</p>
            )}
            <span className="receipt-badge">
              {isDelivery ? "Delivery Order" : "Pickup Order"}
            </span>
          </div>

          <div className="receipt-customer">
            <div className="receipt-row">
              <span>Customer:</span>
              <strong>{order.name}</strong>
            </div>
            <div className="receipt-row">
              <span>Phone:</span>
              <strong className="font-monospace">{order.phone_number}</strong>
            </div>
            <div className="receipt-row">
              <span>Due Time:</span>
              <strong>{order.pickup_time || "ASAP"}</strong>
            </div>
            {isDelivery && (
              <div className="receipt-delivery-block">
                <p className="text-uppercase small text-muted mb-1">Deliver To:</p>
                <p className="mb-0 fw-semibold text-dark">{order.address}</p>
                {order.delivery_apt_number && (
                  <p className="mb-0 text-dark">Apt: {order.delivery_apt_number}</p>
                )}
                {order.delivery_instructions && (
                  <p className="mb-0 text-muted small mt-1">{order.delivery_instructions}</p>
                )}
              </div>
            )}
          </div>

          <div className="mb-4">
            {order.OrderItems?.map((item) => (
              <div key={item.id} className="receipt-item-block">
                <div className="receipt-item-head">
                  <div className="d-flex">
                    <span className="receipt-item-qty">{item.quantity}x</span>
                    <div>
                      <span>
                        {item?.Item?.name}
                        {item?.item_type ? ` (${item.item_type})` : ""}
                      </span>
                      {item?.Item?.chinese_name?.trim() && (
                        <div className="receipt-item-cn">{item.Item.chinese_name}</div>
                      )}
                    </div>
                  </div>
                  <span className="font-monospace">
                    ${Number(item.item_price).toFixed(2)}
                  </span>
                </div>

                {(item.special_instructions ||
                  item.Order_Item_Attributes?.length > 0 ||
                  item.OrderItemSpecialAttributes?.length > 0) && (
                  <div className="receipt-modifiers">
                    {[...(item.Order_Item_Attributes || [])]
                      .sort(compareOrderItemAttributesForReceipt)
                      .map((attr) => {
                        const depth = modifierScopeDepth(attr.option_scope_key);
                        const groupName = attr?.Item_Attribute_Option?.Item_Attribute?.name;
                        const pm = Number(attr?.Item_Attribute_Option?.price_modifier ?? 0);
                        return (
                          <div
                            key={attr.id}
                            className="receipt-modifier-line"
                            style={{
                              paddingLeft: depth ? `${Math.min(depth, 4) * 14}px` : undefined,
                            }}
                          >
                            <span className="text-muted">{depth > 0 ? "↳" : "+"}</span>
                            <span className="flex-grow-1">
                              {depth > 0 && groupName ? (
                                <span className="text-muted text-uppercase small me-1">
                                  {groupName}:
                                </span>
                              ) : null}
                              ({attr.quantity || 1}) {attr?.Item_Attribute_Option?.name}
                              {attr?.Item_Attribute_Option?.chinese_name?.trim() ? (
                                <span className="text-muted ms-1">
                                  ({attr.Item_Attribute_Option.chinese_name})
                                </span>
                              ) : null}
                            </span>
                            <span className="font-monospace">
                              {pm > 0 ? `+$${pm.toFixed(2)}` : ""}
                            </span>
                          </div>
                        );
                      })}

                    {item.OrderItemSpecialAttributes?.map((special) => {
                      const pos =
                        special.position === "left"
                          ? "Left"
                          : special.position === "right"
                            ? "Right"
                            : "";
                      const port =
                        special.portion && special.portion !== "normal" ? special.portion : "";
                      const details = [pos, port].filter(Boolean).join(" ");
                      return (
                        <div key={special.id} className="receipt-modifier-line">
                          <span className="text-muted">+</span>
                          <span className="flex-grow-1">
                            <div>({special.quantity}) {special.name}</div>
                            {details ? (
                              <div className="receipt-modifier-detail">
                                {details} Half/Portion
                              </div>
                            ) : null}
                          </span>
                          <span className="font-monospace">
                            +${Number(special.price || 0).toFixed(2)}
                          </span>
                        </div>
                      );
                    })}

                    {item.special_instructions && (
                      <p className="fst-italic text-muted mb-0 mt-1">
                        &quot;{item.special_instructions}&quot;
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="receipt-summary">
            <div className="receipt-summary-row">
              <span>Subtotal</span>
              <span className="font-monospace fw-semibold">
                ${Number(order.subtotal).toFixed(2)}
              </span>
            </div>

            <div
              className="receipt-summary-row receipt-summary-row--clickable"
              onClick={() => setShowFees((p) => !p)}
            >
              <span>
                Taxes &amp; Fees{" "}
                <FontAwesomeIcon icon={showFees ? faChevronUp : faChevronDown} className="ms-1" />
              </span>
              <span className="font-monospace fw-semibold">
                ${totalFeesAndTaxes.toFixed(2)}
              </span>
            </div>

            {showFees && (
              <div className="receipt-fees-detail">
                {Number(order.tax) > 0 && (
                  <div className="receipt-summary-row">
                    <span>Tax</span>
                    <span>${Number(order.tax).toFixed(2)}</span>
                  </div>
                )}
                {Number(order.delivery_fee) > 0 && (
                  <div className="receipt-summary-row">
                    <span>Delivery</span>
                    <span>${Number(order.delivery_fee).toFixed(2)}</span>
                  </div>
                )}
                {Number(order.service_fee) > 0 && (
                  <div className="receipt-summary-row">
                    <span>Service</span>
                    <span>${Number(order.service_fee).toFixed(2)}</span>
                  </div>
                )}
                {Number(order.processing_fee) > 0 && (
                  <div className="receipt-summary-row">
                    <span>Processing</span>
                    <span>${Number(order.processing_fee).toFixed(2)}</span>
                  </div>
                )}
                {Number(order.convenience_fee) > 0 && (
                  <div className="receipt-summary-row">
                    <span>Convenience</span>
                    <span>${Number(order.convenience_fee).toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}

            {Number(order.tip) > 0 && (
              <div className="receipt-summary-row">
                <span>Tip</span>
                <span className="font-monospace fw-semibold">
                  ${Number(order.tip).toFixed(2)}
                </span>
              </div>
            )}

            {Number(order.discount_total) > 0 && (
              <div className="receipt-summary-row">
                <span>Discount</span>
                <span className="font-monospace fw-semibold">
                  -${Number(order.discount_total).toFixed(2)}
                </span>
              </div>
            )}

            {Number(order.over_charge) > 0 && (
              <div className="receipt-summary-row">
                <span>Extra charge</span>
                <span className="font-monospace fw-semibold">
                  +${Number(order.over_charge).toFixed(2)}
                </span>
              </div>
            )}

            {(order.is_refunded || Number(order.refund_amount) > 0) && (
              <div className="receipt-refund-box">
                <div className="receipt-summary-row receipt-summary-row--refund">
                  <span>Refund</span>
                  <span className="font-monospace fw-semibold">
                    -${Number(order.refund_amount || 0).toFixed(2)}
                  </span>
                </div>
                {order.refund_reason && String(order.refund_reason).trim() ? (
                  <p className="receipt-refund-reason">{order.refund_reason}</p>
                ) : null}
              </div>
            )}

            <div className="receipt-total-row">
              <span className="receipt-total-label">Total</span>
              <span className="receipt-total-value">
                ${Number(order.order_total).toFixed(2)}
              </span>
            </div>

            <div className="receipt-payment-meta">
              <span>{order.payment_method}</span>
              {order.card_last4 && <span>•••• {order.card_last4}</span>}
            </div>
          </div>

          {googleReviewUrl && (
            <a
              href={googleReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="receipt-review-link"
              aria-label="Rate this experience on Google"
            >
              <p className="receipt-review-title">How was your experience?</p>
              <div className="receipt-review-stars">
                {[0, 1, 2, 3, 4].map((star) => (
                  <FontAwesomeIcon key={star} icon={faStar} className="receipt-review-star" />
                ))}
              </div>
              <p className="receipt-review-copy">Tap the stars to leave a Google review</p>
            </a>
          )}

          {order?.Restaurant?.street && (
            <button type="button" className="receipt-btn receipt-btn--outline" onClick={handleNavigate}>
              <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
              Get Directions
            </button>
          )}

          <p className="receipt-thanks">THANK YOU FOR DINING WITH US</p>
        </div>
      </div>

      <div className="receipt-actions">
        <Link to="/order" className="receipt-btn receipt-btn--primary">
          Order Again
        </Link>
        <Link to="/" className="receipt-btn receipt-btn--dark">
          Back Home
        </Link>
      </div>
    </div>
  );
}

export default Receipt;
